import {View} from "@dp-instant/2d";
import {Random} from "@dp-instant/utils";
import {PlayerType} from "@game/coregame/interfaces/PlayerType";
import {CachedGraphics} from "@game/coregame/interfaces/CachedGraphics";
import {gsap} from "gsap/gsap-core";
import {inject, injectable} from "inversify";
import {color} from "@game/coregame/helpers";
import {IBuilding} from "../interfaces/IBuilding";
import {Graphics, Sprite, utils} from "pixi.js-legacy";
import {TypesGame} from "@game/TypesGame";
import {SpritesPool} from "@game/coregame/utils/SpritesPool";
import {CookieModel} from "@game/coregame/model/CookieModel";
import {SkinManager} from "../managers/SkinManager";

const whiteCircleGraphics = (radius = 20) =>
    new Graphics().beginFill(0xffffff).drawCircle(0, 0, radius).endFill();

@injectable()
export class CapitalView extends View {
    @inject(TypesGame.spritesPool)
    protected spritesPool: SpritesPool;

    @inject(TypesGame.cookieModel)
    private cookies: CookieModel;

    @inject(TypesGame.skinManager)
    private skinManager: SkinManager;

    private _radius = 20;
    private base: Sprite;
    private selection: Sprite;

    init(data: IBuilding) {
        this._radius = data.stateRadius;
        this.position.set(...data.statePos);
    }

    protected initBaseGraphics(): CachedGraphics {
        let cachedId = `base${this._radius}`;
        let textureUrl = this.cookies.selectedBuilding?.textureUrl;
        let baseGraphics;
        let diametr: number = this._radius * 2;
        if (textureUrl) {
            baseGraphics = new Sprite(utils.TextureCache[textureUrl]);
            if (baseGraphics) {
                let scale =
                    diametr /
                    Math.max(baseGraphics.getBounds().height, baseGraphics.getBounds().width);
                baseGraphics.scale.set(scale);
                baseGraphics.anchor.set(0.5);
                cachedId = textureUrl;
            }
        } else {
            baseGraphics = whiteCircleGraphics(this._radius);
        }
        return {id: cachedId, obj: baseGraphics};
    }

    public occupiedAnimation(duration = 0.5) {
        gsap.killTweensOf(this.base.scale);
        gsap.fromTo(this.base.scale, {x: 1.5, y: 1.5}, {x: 1, y: 1, duration});
    }
    public shake(duration: number, strength: number) {
        if (!gsap.isTweening(this.base)) {
            const px = this.base.width * strength;
            const py = this.base.height * strength;
            const x = Random.rangeFloat(-px, px);
            const y = Random.rangeFloat(-py, py);
            gsap.fromTo(this.base.position, {x, y}, {x: 0, y: 0, ease: "elastic.out", duration});
            // const rotation = Math.atan2(y, x);
            // gsap.fromTo(this.base, {rotation}, {rotation: 0, ease: "elastic.out", duration});
        }
    }
    public showSelection(color?: number, alpha = 0.5) {
        gsap.killTweensOf(this);
        if (!this.selection) {
            this.selection = this.spritesPool.fromDisplayObject(
                `base_selection${this._radius}`,
                () => whiteCircleGraphics(2 * this._radius)
            );
            this.selection.anchor.set(0.5);
            this.selection.alpha = 0;
            this.addChildAt(this.selection, 0);
        }
        if (color) {
            this.selection.tint = color;
            gsap.to(this.selection, {alpha});
        } else {
            gsap.to(this.selection, {alpha: 0});
        }
    }
    public updateSkin(owner: PlayerType = PlayerType.Default, isActive = true) {
        const [, baseColor] = this.skinManager.getColorBy(owner);
        const textureUrl = this.skinManager.getBuildingTextureBy(
            isActive ? owner : PlayerType.Neutral
        );
        this.base && this.removeChild(this.base);
        this.base = this.spritesPool.fromDisplayObject(textureUrl, () => {
            const sprite = new Sprite(utils.TextureCache[textureUrl]);
            const bounds = sprite.getBounds();
            const scale = (this._radius * 2) / Math.max(bounds.height, bounds.width);
            sprite.anchor.set(0.5);
            sprite.scale.set(scale);
            return sprite;
        });
        this.base.anchor.set(0.5);
        this.addChild(this.base);
        this.base.tint = owner === PlayerType.Neutral ? 0xffffff : color.fromHex(baseColor);
    }
    private _active = true;

    setActive(value: bool, duration?: number) {
        this._active = value;
        gsap.killTweensOf(this, "alpha");
        gsap.to(this, {alpha: this._active ? 1 : 0.25, duration});
    }
}
